import { env } from "../config/env";
import { sessionStore } from "../auth/session-store";
import type { AuthSession, JsonRecord } from "../types/supplier";

type RequestOptions = Omit<RequestInit, "body"> & {
  body?: JsonRecord | BodyInit | null;
  auth?: boolean;
  retry?: boolean;
  baseUrl?: string;
  query?: Record<string, string | number | undefined>;
};

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

let refreshPromise: Promise<AuthSession> | null = null;

function buildUrl(
  path: string,
  baseUrl: string,
  query?: RequestOptions["query"],
) {
  const url = new URL(
    `${baseUrl.replace(/\/$/, "")}/${path.replace(/^\//, "")}`,
  );
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined) url.searchParams.set(key, String(value));
  }
  return url;
}

async function parseResponse(response: Response) {
  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) {
    return response.text().then((text) => (text ? { message: text } : {}));
  }
  return response.json().catch(() => ({}));
}

async function refreshSession() {
  if (refreshPromise) return refreshPromise;
  const current = sessionStore.load();
  if (!current) throw new ApiError("로그인이 필요합니다.", 401);

  refreshPromise = fetch(buildUrl("/v1/auth/refresh", env.supplierApiUrl), {
    method: "POST",
    headers: { "content-type": "application/json; charset=utf-8" },
    body: JSON.stringify({ refreshToken: current.refreshToken }),
  })
    .then(async (response) => {
      const data = (await parseResponse(response)) as Partial<AuthSession> &
        JsonRecord;
      if (
        !response.ok ||
        typeof data.accessToken !== "string" ||
        typeof data.refreshToken !== "string"
      ) {
        sessionStore.clear();
        throw new ApiError("세션이 만료되었습니다.", response.status);
      }
      const next = {
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
      };
      sessionStore.save(next);
      return next;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

export async function apiRequest<T>(
  path: string,
  {
    auth = true,
    retry = true,
    baseUrl = env.supplierApiUrl,
    query,
    headers,
    body,
    ...init
  }: RequestOptions = {},
): Promise<T> {
  const session = sessionStore.load();
  const isBodyInit =
    typeof body === "string" ||
    body instanceof Blob ||
    body instanceof FormData ||
    body instanceof URLSearchParams ||
    body instanceof ArrayBuffer;

  const response = await fetch(buildUrl(path, baseUrl, query), {
    ...init,
    headers: {
      ...(!isBodyInit && body
        ? { "content-type": "application/json; charset=utf-8" }
        : {}),
      ...(auth && session
        ? { authorization: `Bearer ${session.accessToken}` }
        : {}),
      ...headers,
    },
    body: body == null ? undefined : isBodyInit ? body : JSON.stringify(body),
  });

  if (response.status === 401 && auth && retry && session?.refreshToken) {
    await refreshSession();
    return apiRequest<T>(path, {
      auth,
      retry: false,
      baseUrl,
      query,
      headers,
      body,
      ...init,
    });
  }

  const data = (await parseResponse(response)) as JsonRecord;
  if (!response.ok) {
    const error = data.error;
    const errorRecord =
      error && typeof error === "object" ? (error as JsonRecord) : undefined;
    const code =
      typeof errorRecord?.code === "string" ? errorRecord.code : undefined;
    const message =
      typeof errorRecord?.message === "string"
        ? errorRecord.message
        : typeof data.message === "string"
          ? data.message
          : `HTTP ${response.status}`;
    throw new ApiError(code ? `${code}: ${message}` : message, response.status, code, data);
  }

  return data as T;
}
